import { Button } from '@upme/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@upme/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@upme/ui/components/popover';
import { cn } from '@upme/ui/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Props {
  value?: string;
  onChange: (tz: string) => void;
}

// "GMT+5", "GMT+5:30", "GMT-03:30" → "UTC+5", "UTC+5:30", "UTC-3:30"
function formatOffset(tz: string, now: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    timeZoneName: 'shortOffset',
  }).formatToParts(now);
  const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
  // "GMT" alone = UTC+0
  if (raw === 'GMT') return 'UTC+0';
  return raw.replace(/^GMT/, 'UTC').replace(/([+-])0(\d)/, '$1$2');
}

export function TimezoneCombobox({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const entries = useMemo(() => {
    const now = new Date();
    return Intl.supportedValuesOf('timeZone').map((tz) => ({
      tz,
      offset: formatOffset(tz, now),
    }));
  }, []);

  const selectedOffset = useMemo(() => {
    if (!value) return undefined;
    return entries.find((e) => e.tz === value)?.offset;
  }, [entries, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between">
          <span className="truncate">
            {value ? (
              <>
                {value}
                {selectedOffset && <span className="ml-2 text-muted-foreground">{selectedOffset}</span>}
              </>
            ) : (
              'Выберите часовой пояс'
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]">
        <Command>
          <CommandInput placeholder="Поиск (напр. Moscow, +5)" />
          <CommandList className="max-h-80">
            <CommandEmpty>Не найдено</CommandEmpty>
            <CommandGroup>
              {entries.map(({ tz, offset }) => (
                <CommandItem
                  key={tz}
                  value={`${tz} ${offset}`}
                  onSelect={() => {
                    onChange(tz);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === tz ? 'opacity-100' : 'opacity-0')} />
                  <span className="flex-1 truncate">{tz}</span>
                  <span className="ml-2 text-xs text-muted-foreground tabular-nums">{offset}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
